import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';

import { Plus, Search, Loader2, Filter, Download, Sheet } from 'lucide-react';
import { toCSV, downloadCSV, openInGoogleSheets } from '../../../utils/csvExport';
import { getAllBundles, saveBundle } from '../../../services/firestore/formularyService';
import type { Bundle, ProductStatus } from '../../../types/formulary';
import type { ClinicalTier } from '../../../types/index';

const TIER_COLORS: Record<number, string> = {
  1: 'text-optimal bg-green-50 border-green-200',
  2: 'text-gold bg-yellow-50 border-yellow-200',
  3: 'text-accent-light bg-red-50 border-red-200',
  4: 'text-purple-600 bg-purple-50 border-purple-200',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'text-optimal',
  draft: 'text-text-muted',
  discontinued: 'text-critical',
};

export function BundleList() {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState<ClinicalTier | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<ProductStatus | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadBundles();
  }, []);

  async function loadBundles() {
    try {
      setLoading(true);
      const data = await getAllBundles();
      setBundles(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bundles');
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    return bundles.filter((b) => {
      if (search && !b.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterTier !== 'all' && b.tier !== filterTier) return false;
      if (filterStatus !== 'all' && b.status !== filterStatus) return false;
      return true;
    });
  }, [bundles, search, filterTier, filterStatus]);

  async function toggleStatus(bundle: Bundle) {
    const newStatus: ProductStatus = bundle.status === 'active' ? 'draft' : 'active';
    const updated = { ...bundle, status: newStatus };
    try {
      await saveBundle(updated);
      setBundles((prev) => prev.map((b) => (b.id === bundle.id ? updated : b)));
    } catch (err) {
      alert('Failed to update status: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
        <p className="font-medium">Error loading bundles</p>
        <p className="text-base mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-3xl font-display text-gold">Bundles</h2>
          <p className="text-base text-text-muted">{bundles.length} total, {filtered.length} shown</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const csv = toCSV(filtered.map((b) => ({
                '#': b.bundleNumber, Name: b.name, Tier: b.tier,
                Products: b.products.map((p) => p.productName).join('; '),
                'Product Count': b.products.length,
                'Price Range': b.priceRange, Gender: b.gender,
                Target: b.target, Status: b.status,
                'Requires Provider': b.requiresProvider,
                Contraindications: b.contraindications,
                Warnings: b.warnings,
              })), [
                { key: '#', label: '#' }, { key: 'Name', label: 'Name' },
                { key: 'Tier', label: 'Tier' }, { key: 'Products', label: 'Products' },
                { key: 'Product Count', label: 'Product Count' },
                { key: 'Price Range', label: 'Price Range' }, { key: 'Gender', label: 'Gender' },
                { key: 'Target', label: 'Target' }, { key: 'Status', label: 'Status' },
                { key: 'Requires Provider', label: 'Requires Provider' },
                { key: 'Contraindications', label: 'Contraindications' },
                { key: 'Warnings', label: 'Warnings' },
              ]);
              downloadCSV(csv, 'hemologix-bundles.csv');
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors"
          >
            <Download size={16} />
            CSV
          </button>
          <button
            onClick={() => {
              const csv = toCSV(filtered.map((b) => ({
                '#': b.bundleNumber, Name: b.name, Tier: b.tier,
                Products: b.products.map((p) => p.productName).join('; '),
                'Price Range': b.priceRange, Gender: b.gender, Target: b.target,
              })), [
                { key: '#', label: '#' }, { key: 'Name', label: 'Name' },
                { key: 'Tier', label: 'Tier' }, { key: 'Products', label: 'Products' },
                { key: 'Price Range', label: 'Price Range' }, { key: 'Gender', label: 'Gender' },
                { key: 'Target', label: 'Target' },
              ]);
              openInGoogleSheets(csv, 'Hemologix Bundles');
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors"
          >
            <Sheet size={16} className="text-optimal" />
            Sheets
          </button>
          <Link
            to="/admin/bundles/new"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent/80 text-white rounded-lg text-base font-medium transition-colors"
          >
            <Plus size={16} />
            Add Bundle
          </Link>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search bundles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-bg-elevated border border-bg-tertiary rounded-lg text-base text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold/50"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-base border transition-colors ${
              showFilters ? 'border-gold/50 text-gold' : 'border-bg-tertiary text-text-secondary hover:text-text-primary'
            }`}
          >
            <Filter size={16} />
            Filters
          </button>
        </div>

        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-bg-tertiary"
          >
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value === 'all' ? 'all' : (Number(e.target.value) as ClinicalTier))}
              className="px-3 py-2 bg-bg-elevated border border-bg-tertiary rounded-lg text-base text-text-primary focus:outline-none focus:border-gold/50"
            >
              <option value="all">All Tiers</option>
              <option value="1">Tier 1</option>
              <option value="2">Tier 2</option>
              <option value="3">Tier 3</option>
              <option value="4">Tier 4</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as ProductStatus | 'all')}
              className="px-3 py-2 bg-bg-elevated border border-bg-tertiary rounded-lg text-base text-text-primary focus:outline-none focus:border-gold/50"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="discontinued">Discontinued</option>
            </select>
          </motion.div>
        )}
      </div>

      {/* Table */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead>
              <tr className="border-b border-bg-tertiary text-text-muted text-left">
                <th className="px-4 py-3 font-medium">#</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Tier</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell">Products</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Price</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Gender</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((bundle, i) => (
                <motion.tr
                  key={bundle.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.01 }}
                  onClick={() => navigate(`/admin/bundles/${bundle.id}`)}
                  className="border-b border-bg-tertiary/50 hover:bg-bg-elevated cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-text-muted">{bundle.bundleNumber}</td>
                  <td className="px-4 py-3 font-medium text-text-primary">{bundle.name}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium border ${TIER_COLORS[bundle.tier] || ''}`}>
                      T{bundle.tier}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-text-secondary">
                    {bundle.products.length}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-text-secondary">
                    {bundle.priceRange}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="capitalize text-text-secondary">{bundle.gender}</span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStatus(bundle);
                      }}
                      className={`text-sm font-medium capitalize ${STATUS_COLORS[bundle.status] || ''} hover:underline`}
                    >
                      {bundle.status}
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-text-muted">
            <p>No bundles match your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}
